import { Bell, Building2, CheckCircle2, Clock, KeyRound, Link2, Search, ShieldCheck, Users } from "lucide-react";
import { EmptyState } from "@/components/ui/feedback";
import { Table, TableWrapper } from "@/components/ui/table";
import {
  ActionLink,
  CrossModuleLink,
  SpecimenSection,
} from "@/features/system-states/system-state-page";

function EmptyTable({
  columns,
  title,
  description,
  icon,
  tone = "default",
  action,
  meta,
}: {
  columns: string[];
  title: string;
  description: string;
  icon: React.ReactNode;
  tone?: "default" | "positive";
  action?: React.ReactNode;
  meta?: React.ReactNode;
}) {
  return (
    <TableWrapper>
      <Table>
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c}>{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr>
            <td colSpan={columns.length} className="p-0">
              <EmptyState
                card
                title={title}
                description={description}
                icon={icon}
                tone={tone}
                action={action}
                meta={meta}
                className="border-0 rounded-none bg-transparent"
              />
            </td>
          </tr>
        </tbody>
      </Table>
    </TableWrapper>
  );
}

export default function SysEmpty() {
  return (
    <>
      <SpecimenSection label="Operations">
        <div className="flex flex-col gap-5">
          <EmptyTable
            columns={["Transfer", "Amount", "Destination", "Status", "Created"]}
            title="No transfers yet"
            description="No transfers have been initiated for this tenant. Create a transfer or set up a recurring schedule to get started."
            icon={<Search aria-hidden className="text-muted" />}
            action={<ActionLink href="/transfers/new">Initiate transfer</ActionLink>}
          />
          <EmptyTable
            columns={["Wallet", "Network", "Balance", "Status"]}
            title="No wallets yet"
            description="No wallets exist for this tenant. Add a custody wallet to begin receiving and sending funds."
            icon={<Building2 aria-hidden className="text-muted" />}
            action={<ActionLink href="/wallets/new">Add wallet</ActionLink>}
          />
          <EmptyTable
            columns={["Approval", "Initiator", "Amount", "Requested"]}
            title="No pending approvals"
            description="There are no approvals waiting on you. Everything assigned to your role is up to date."
            icon={<CheckCircle2 aria-hidden className="text-success" />}
            tone="positive"
            meta="You are notified the moment a new approval is assigned."
          />
        </div>
      </SpecimenSection>

      <SpecimenSection label="Compliance">
        <div className="flex flex-col gap-5">
          <EmptyTable
            columns={["Screening", "Counterparty", "Result", "Checked"]}
            title="No screening hits"
            description="No sanctions or AML screening hits require review. New counterparties are screened automatically."
            icon={<ShieldCheck aria-hidden className="text-success" />}
            tone="positive"
            meta="Screening is performed by Chainalysis on every inbound and outbound transfer."
          />
          <EmptyTable
            columns={["Event", "Actor", "Entity", "Time"]}
            title="No audit events yet"
            description="Audit activity will appear here as soon as privileged actions are performed in this tenant."
            icon={<Clock aria-hidden className="text-muted" />}
            meta="Audit history is immutable and retained for the full compliance window."
          />
          <EmptyTable
            columns={["Record", "Counterparty", "Jurisdiction", "Status"]}
            title="No travel rule records"
            description="No travel-rule information has been exchanged. Originator and beneficiary data is captured automatically for qualifying transfers."
            icon={<Link2 aria-hidden className="text-muted" />}
            meta="Required above the FATF threshold for cross-border transfers."
          />
        </div>
      </SpecimenSection>

      <SpecimenSection label="Infrastructure">
        <div className="flex flex-col gap-5">
          <EmptyTable
            columns={["Key", "Scope", "Environment", "Created"]}
            title="No API keys"
            description="No API keys have been issued for this environment. Create a scoped key to integrate with Alloy programmatically."
            icon={<KeyRound aria-hidden className="text-muted" />}
            action={<CrossModuleLink>Create API key</CrossModuleLink>}
          />
          <EmptyTable
            columns={["Endpoint", "Events", "Status", "Last delivery"]}
            title="No webhooks configured"
            description="No webhook endpoints are registered. Add an endpoint to receive signed event notifications."
            icon={<Bell aria-hidden className="text-muted" />}
            action={<CrossModuleLink>Add endpoint</CrossModuleLink>}
          />
          <EmptyTable
            columns={["Incident", "Severity", "Impact", "Opened"]}
            title="No active incidents"
            description="All systems are operating normally. There are no open incidents affecting this tenant."
            icon={<CheckCircle2 aria-hidden className="text-success" />}
            tone="positive"
            meta="Status is published for the last 90 days."
          />
        </div>
      </SpecimenSection>

      <SpecimenSection label="Tenant membership">
        <div className="grid gap-3 lg:grid-cols-2">
          <EmptyState
            card
            title="No members yet"
            description="Invite teammates to this tenant and assign them a role. Members only see what their role allows."
            icon={<Users aria-hidden className="text-muted" />}
            action={<CrossModuleLink>Invite member</CrossModuleLink>}
          />
          <EmptyState
            card
            title="No teams configured"
            description="Group members into teams to manage permissions at scale instead of per user."
            icon={<Building2 aria-hidden className="text-muted" />}
            action={<CrossModuleLink>Create team</CrossModuleLink>}
          />
        </div>
      </SpecimenSection>
    </>
  );
}
